import type { ProductModifierSchema } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

import type { ProductModifier } from "~/providers/product-provider";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { useProductModifierStore } from "~/stores/use-product-modifier-store";
import { usePermissionManager } from "./use-permission-manager";

export const useProductModifierManager = () => {
  const { t } = useTranslation("common");

  const { handleProductModifierDelete, handleProductModifierUpdate } =
    useProductModifierUpdates();

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  const { hasPermission } = usePermissionManager();

  const { setProductModifierFormOpen, setSelectedProductModifier } =
    useProductModifierStore();

  const { mutate: create } = api.productModifier.create.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(
          t("create_item_success", {
            item: t("ProductModifier.product_modifier"),
          }),
        ),
      );
      await handleProductModifierUpdate(result);
    },
    onError() {
      toast.error(
        capitalize(
          t("create_item_error", {
            item: t("ProductModifier.product_modifier"),
          }),
        ),
      );
    },
  });

  const { mutate: update } = api.productModifier.update.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        capitalize(
          t("update_item_success", {
            item: t("ProductModifier.product_modifier"),
          }),
        ),
      );
      await handleProductModifierUpdate(variables.id);
    },
    onError() {
      toast.error(
        capitalize(
          t("update_item_error", {
            item: t("ProductModifier.product_modifier"),
          }),
        ),
      );
    },
  });

  const { mutate: deleteProduct } = api.productModifier.delete.useMutation({
    onSuccess(_, variables) {
      toast.success(
        capitalize(
          t("delete_item_success", {
            item: t("ProductModifier.product_modifier"),
          }),
        ),
      );
      handleProductModifierDelete(variables);
    },
    onError() {
      toast.error(
        capitalize(
          t("delete_item_fail", {
            item: t("ProductModifier.product_modifier"),
          }),
        ),
      );
    },
  });

  const createProductModifier = async (
    productModifier: z.infer<typeof ProductModifierSchema>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_product,
      label: t("Permissions.create_product_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      create(productModifier, {
        onSuccess: () => {
          resolve(true);
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const updateProductModifier = async (
    id: string,
    productModifier: z.infer<typeof ProductModifierSchema>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product,
      label: t("Permissions.update_product_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      update(
        {
          ...productModifier,
          id,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(false);
          },
        },
      );
    });
  };

  const deleteProductModifier = async (id: string) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_product,
      label: t("Permissions.delete_product_label"),
    });

    if (!permission) return false;

    const result = await showDialog({
      title: t("before_delete_product_title"),
      message: t("before_delete_product_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return false;

    return new Promise<boolean>((resolve) => {
      deleteProduct(id, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const openProductModifierDialog = (productModifier?: ProductModifier) => {
    setProductModifierFormOpen(true);
    setSelectedProductModifier(productModifier);
  };

  return {
    openProductModifierDialog,
    createProductModifier,
    updateProductModifier,
    deleteProductModifier,
  };
};

export const useProductModifierUpdates = () => {
  const utils = api.useUtils();

  const handleProductModifierUpdate = useCallback(
    async (modifierId: string) => {
      const result = await utils.productModifier.getById.fetch(modifierId);
      await utils.productModifierGroup.getAll.invalidate();

      void utils.productModifier.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [
      utils.productModifier.getAll,
      utils.productModifier.getById,
      utils.productModifierGroup.getAll,
    ],
  );

  const handleProductModifierDelete = useCallback(
    (modifierId: string) => {
      void utils.productModifier.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== modifierId);
      });
    },
    [utils.productModifier.getAll],
  );

  return {
    handleProductModifierUpdate,
    handleProductModifierDelete,
  };
};
